<template>
  <div ref="rootRef" style="position: relative">
    <button
      type="button"
      class="field-input"
      style="
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        text-align: left;
      "
      @click="isOpen = !isOpen"
    >
      <span :style="{ color: modelValue ? 'var(--ink)' : 'var(--ink-dim)' }">{{
        modelValue ? displayValue : placeholder
      }}</span>
      <span style="color: var(--ink-dim); font-size: 12px">▾</span>
    </button>

    <div v-if="isOpen" class="datepicker-dropdown">
      <!-- Header -->
      <div class="datepicker-header">
        <button type="button" class="datepicker-nav" @click="prevMonth">
          ‹
        </button>
        <span class="datepicker-month">{{ monthLabel }}</span>
        <button type="button" class="datepicker-nav" @click="nextMonth">
          ›
        </button>
      </div>

      <!-- Week days -->
      <div class="datepicker-grid">
        <span v-for="d in weekDays" :key="d" class="datepicker-dow">{{
          d
        }}</span>

        <!-- Leading empty cells -->
        <span v-for="n in leadingBlanks" :key="'b' + n" />

        <!-- Day cells -->
        <button
          v-for="day in daysInMonth"
          :key="day"
          type="button"
          class="datepicker-day"
          :class="{
            'is-today': isToday(day),
            'is-selected': isSelected(day),
            'is-disabled': isDisabled(day),
          }"
          :disabled="isDisabled(day)"
          @click="selectDay(day)"
        >
          {{ day }}
        </button>
      </div>

      <!-- Footer -->
      <div class="datepicker-footer">
        <button type="button" class="datepicker-action" @click="clear">
          Effacer
        </button>
        <button type="button" class="datepicker-action" @click="selectToday">
          Aujourd'hui
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { onClickOutside } from '@vueuse/core';

const props = defineProps<{
  modelValue: Date | null | undefined;
  placeholder?: string;
  min?: Date | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: Date | null];
}>();

const rootRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);

const cursor = ref(
  props.modelValue ? dayjs(props.modelValue) : dayjs().startOf('month'),
);

onClickOutside(rootRef, () => {
  isOpen.value = false;
});

const weekDays = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const monthLabel = computed(() =>
  cursor.value.format('MMMM YYYY').replace(/^\w/, (c) => c.toUpperCase()),
);

const daysInMonth = computed(() => cursor.value.daysInMonth());

// Monday-based: dayjs uses 0=Sunday, so shift
const leadingBlanks = computed(() => {
  const dow = cursor.value.startOf('month').day();
  return dow === 0 ? 6 : dow - 1;
});

const displayValue = computed(() =>
  props.modelValue ? dayjs(props.modelValue).format('DD/MM/YYYY') : '',
);

function getDay(day: number) {
  return cursor.value.date(day);
}

function isToday(day: number) {
  return getDay(day).isSame(dayjs(), 'day');
}

function isSelected(day: number) {
  return props.modelValue
    ? getDay(day).isSame(dayjs(props.modelValue), 'day')
    : false;
}

function isDisabled(day: number) {
  if (!props.min) return false;
  return getDay(day).isBefore(dayjs(props.min), 'day');
}

function selectDay(day: number) {
  if (isDisabled(day)) return;
  emit('update:modelValue', getDay(day).toDate());
  isOpen.value = false;
}

function selectToday() {
  const today = dayjs().startOf('day');
  if (!props.min || !today.isBefore(dayjs(props.min), 'day')) {
    emit('update:modelValue', today.toDate());
    cursor.value = today;
    isOpen.value = false;
  }
}

function clear() {
  emit('update:modelValue', null);
  isOpen.value = false;
}

function prevMonth() {
  cursor.value = cursor.value.subtract(1, 'month');
}

function nextMonth() {
  cursor.value = cursor.value.add(1, 'month');
}

watch(
  () => props.modelValue,
  (v) => {
    if (v) cursor.value = dayjs(v).startOf('month');
  },
);
</script>
